import { useEffect, useRef } from "react";
import { type Message } from "../types/session";

interface MessagesAreaProps {
  messages: Message[];
  mode?: 'default' | 'user';
  consultantName?: string;
  consultantImage?: string | null;
  isTyping?: boolean;
}

export default function MessagesArea({
  messages,
  mode = 'default',
  consultantName,
  consultantImage,
  isTyping = false,
}: MessagesAreaProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-2.5 sm:px-6 py-4 sm:py-5 flex flex-col gap-2.5 sm:gap-3">
      {mode === 'user' && (
        <div className="mb-2 p-2 sm:p-2.5 rounded-lg bg-[#8b5cf6]/10 border border-[#8b5cf6]/20 text-center text-[0.7rem] sm:text-[0.75rem] text-[#c4b5fd]">
          As respostas são geradas por inteligência artificial e destinam-se exclusivamente a entretenimento.
        </div>
      )}
      {messages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          mode={mode}
          consultantName={consultantName}
          consultantImage={consultantImage}
        />
      ))}
      {isTyping && (
        <div className="flex items-end gap-2 sm:gap-2.5 self-start max-w-[90%] sm:max-w-[85%] animate-fadeIn">
          <ConsultantAvatar name={consultantName} image={consultantImage} size="sm" />
          <div className="flex flex-col gap-1">
            <div className="text-[0.62rem] sm:text-[0.68rem] text-text-soft pl-1">
              {consultantName} · agora
            </div>
            <div className="rounded-2xl rounded-bl-md px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0d0f1c]/95 border border-border-soft/60 backdrop-blur-sm">
              <TypingIndicator />
            </div>
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}

/* ── Consultant Avatar ── */

function ConsultantAvatar({
  name,
  image,
  size = 'sm',
}: {
  name?: string;
  image?: string | null;
  size?: 'sm' | 'md';
}) {
  const sizeClasses = size === 'md' ? 'w-9 h-9 sm:w-10 sm:h-10 text-[0.85rem] sm:text-[0.9rem]' : 'w-7 h-7 sm:w-8 sm:h-8 text-[0.72rem] sm:text-[0.78rem]';
  const initials = (name || 'C').substring(0, 2).toUpperCase();

  if (image) {
    return (
      <img
        src={image}
        alt={name || 'Consultor'}
        className={`${sizeClasses} rounded-full object-cover border-2 border-accent/40 shadow-[0_0_12px_rgba(226,183,20,0.15)] flex-shrink-0`}
      />
    );
  }

  return (
    <div
      className={`${sizeClasses} rounded-full bg-gradient-to-br from-accent to-[#e86fde] flex items-center justify-center font-semibold flex-shrink-0 shadow-[0_0_12px_rgba(226,183,20,0.15)]`}
    >
      {initials}
    </div>
  );
}

/* ── User Avatar ── */

function UserAvatar({ size = 'sm' }: { size?: 'sm' | 'md' }) {
  const sizeClasses = size === 'md' ? 'w-9 h-9 sm:w-10 sm:h-10 text-[0.85rem] sm:text-[0.9rem]' : 'w-7 h-7 sm:w-8 sm:h-8 text-[0.72rem] sm:text-[0.78rem]';

  return (
    <div
      className={`${sizeClasses} rounded-full bg-gradient-to-br from-[#3b82f6] to-[#8b5cf6] flex items-center justify-center font-semibold flex-shrink-0 shadow-[0_0_8px_rgba(99,102,241,0.15)]`}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    </div>
  );
}

/* ── Typing Indicator ── */

function TypingIndicator() {
  return (
    <div className="flex gap-1.5 items-center py-0.5">
      <span className="text-[0.62rem] sm:text-[0.68rem] text-text-soft">a escrever</span>
      <div className="flex gap-[3px]">
        <div className="w-[5px] h-[5px] bg-accent/60 rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '0.8s' }} />
        <div className="w-[5px] h-[5px] bg-accent/60 rounded-full animate-bounce" style={{ animationDelay: '160ms', animationDuration: '0.8s' }} />
        <div className="w-[5px] h-[5px] bg-accent/60 rounded-full animate-bounce" style={{ animationDelay: '320ms', animationDuration: '0.8s' }} />
      </div>
    </div>
  );
}

/* ── Message Bubble ── */

function MessageBubble({
  message,
  mode,
  consultantName,
  consultantImage,
}: {
  message: Message;
  mode: 'default' | 'user';
  consultantName?: string;
  consultantImage?: string | null;
}) {
  if (message.sender === "system") {
    return (
      <div className="self-center text-[0.65rem] sm:text-[0.7rem] text-text-soft px-3 sm:px-4 py-1.5 rounded-full border border-dashed border-border-soft/50 my-1 bg-white/[0.02]">
        {message.content}
      </div>
    );
  }

  const isUser = mode === 'user' ? message.sender === 'client' : message.sender === 'assistant';

  if (isUser) {
    return (
      <div className="flex items-end gap-2 sm:gap-2.5 self-end max-w-[90%] sm:max-w-[80%] animate-fadeIn">
        <div className="flex flex-col gap-1 items-end min-w-0">
          <div className="text-[0.62rem] sm:text-[0.68rem] text-text-soft pr-1">
            {mode === 'user' ? 'Você' : `Você (${consultantName || 'Consultor'})`} · {message.timestamp}
          </div>
          <div
            className="rounded-2xl rounded-br-md px-3 sm:px-4 py-2 sm:py-2.5 text-[0.78rem] sm:text-[0.82rem] leading-relaxed bg-gradient-to-br from-[#1a1d3a] to-[#12142a] border border-[#2a2f52]/60 text-text shadow-[0_2px_8px_rgba(0,0,0,0.2)] break-words"
            dangerouslySetInnerHTML={{ __html: message.content }}
          />
        </div>
        <UserAvatar />
      </div>
    );
  }

  return (
    <div className="flex items-end gap-2 sm:gap-2.5 self-start max-w-[90%] sm:max-w-[80%] animate-fadeIn">
      <ConsultantAvatar name={consultantName} image={consultantImage} />
      <div className="flex flex-col gap-1 min-w-0">
        <div className="text-[0.62rem] sm:text-[0.68rem] text-text-soft pl-1">
          {mode === 'user' ? consultantName || 'Consultor' : 'Cliente'} · {message.timestamp}
        </div>
        <div
          className="rounded-2xl rounded-bl-md px-3 sm:px-4 py-2 sm:py-2.5 text-[0.78rem] sm:text-[0.82rem] leading-relaxed bg-[#0d0f1c]/95 border border-border-soft/60 backdrop-blur-sm text-text shadow-[0_2px_8px_rgba(0,0,0,0.15)] break-words"
          dangerouslySetInnerHTML={{ __html: message.content }}
        />
      </div>
    </div>
  );
}